import React, { useEffect, useState } from "react";
import { Briefcase, Landmark, Home, Save, ChevronRight, ShieldCheck, Scale as ScaleIcon } from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import Skeleton from "@/components/jea/Skeleton";
import { demoClient, useDemo } from "@/pages/demo/DemoShell";
import { cn } from "@/lib/utils";

const PATHWAYS = [
  {
    key: "wage_garnishment",
    label: "Wage Garnishment",
    icon: Briefcase,
    tagline: "Employer of record surfaced. CCPA cap validated. Ready to serve.",
    checks: [
      "Employer confirmed via The Work Number",
      "CCPA 25% cap applied to disposable earnings",
      "State head-of-household exemption checked",
    ],
    recovery: 74,
  },
  {
    key: "bank_garnishment",
    label: "Bank Levy",
    icon: Landmark,
    tagline: "Operating accounts identified. Anti-comingling rules applied.",
    checks: [
      "Active depository accounts located",
      "30-day dispute clock triggered",
      "Anti-comingling exemption reviewed",
    ],
    recovery: 61,
  },
  {
    key: "property_levy",
    label: "Property Levy",
    icon: Home,
    tagline: "Real property + junior liens. Homestead exemption modeled.",
    checks: [
      "Real property confirmed with county recorder",
      "Homestead exemption limit applied",
      "Junior lien position calculated",
    ],
    recovery: 48,
  },
];

const DemoEnforcementPage = () => {
  const demo = useDemo();
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState("wage_garnishment");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await demoClient.get("/demo/enforcement/plans");
        setPlans(data.plans || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const nudge = () => demo?.openSignUp("saving enforcement plans");
  const active = PATHWAYS.find((p) => p.key === selected);
  const savedForPathway = plans.filter((p) => p.pathway === selected);

  return (
    <div className="space-y-6" data-testid="demo-enforcement-page">
      <div>
        <div className="jea-caption">Post-Judgment Enforcement</div>
        <h1 className="text-[28px] font-bold text-white">Enforcement</h1>
        <p className="text-text-secondary text-[14px] mt-1">
          Three guided pathways. State-specific rules and exemptions applied automatically.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-tour="enforcement-pathways">
        {PATHWAYS.map((p) => {
          const Icon = p.icon;
          const active = p.key === selected;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => setSelected(p.key)}
              className={cn(
                "text-left rounded-[12px] border-2 p-5 transition-all",
                active
                  ? "border-accent-pink bg-accent-pink/10"
                  : "border-border-default bg-bg-secondary hover:border-accent-pink/40 hover:bg-bg-tertiary"
              )}
              data-testid={`demo-pathway-${p.key}`}
            >
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-11 h-11 rounded-[8px] flex items-center justify-center",
                  active ? "bg-accent-pink text-[#091424]" : "bg-bg-tertiary text-accent-pink"
                )}>
                  <Icon size={20} />
                </div>
                <div className="min-w-0">
                  <div className="text-white text-[15px] font-bold">{p.label}</div>
                  <div className="jea-mono text-accent-pink font-bold text-[13px]">
                    {p.recovery}% avg recovery
                  </div>
                </div>
              </div>
              <div className="text-text-secondary text-[13px] mt-3">{p.tagline}</div>
            </button>
          );
        })}
      </div>

      <JeaCard>
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={16} className="text-accent-pink" />
          <h2 className="text-white font-bold text-[16px]">Rule Checks Applied — {active?.label}</h2>
        </div>
        <ul className="space-y-2">
          {(active?.checks || []).map((c, i) => (
            <li key={i} className="flex items-start gap-2 text-text-secondary text-[13px]" data-testid={`demo-enforcement-check-${selected}-${i}`}>
              <ChevronRight size={14} className="text-accent-pink shrink-0 mt-[2px]" />
              <span>{c}</span>
            </li>
          ))}
        </ul>
        <div className="mt-6 flex justify-end">
          <JeaButton className="gap-2" onClick={nudge} data-testid="demo-enforcement-save">
            <Save size={14} /> Save Enforcement Plan
          </JeaButton>
        </div>
      </JeaCard>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <ScaleIcon size={14} className="text-text-secondary" />
          <div className="jea-caption">Saved Enforcement Plans ({savedForPathway.length} for {active?.label})</div>
        </div>
        {loading ? <Skeleton className="h-32" /> : plans.length === 0 ? (
          <div className="text-text-secondary text-[13px] bg-bg-tertiary rounded-[8px] p-6">
            No saved plans yet. Sign up to build and version your enforcement strategy.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="demo-enforcement-plans">
            {plans.map((plan) => (
              <JeaCard key={plan.id} className="!p-4">
                <div className="jea-caption">{(plan.pathway || "").replace("_", " ").toUpperCase()}</div>
                <div className="text-white text-[14px] font-semibold mt-1">{plan.debtor_name}</div>
                <p className="text-text-secondary text-[12px] mt-1">{plan.summary}</p>
                <div className="mt-3 flex flex-wrap gap-1">
                  {(plan.tags || []).slice(0, 2).map((t, i) => (
                    <span key={i} className="text-[10px] bg-bg-tertiary text-text-secondary px-2 py-1 rounded-full tracking-[0.06em] font-semibold">
                      {t}
                    </span>
                  ))}
                </div>
              </JeaCard>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoEnforcementPage;
